import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronLeft, Plus, Trash2, Check, Clock, Calendar, Edit2, X, BellOff, BellRing } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../services/api';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const NotificationsManager = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [pastReminders, setPastReminders] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNewReminderDialog, setShowNewReminderDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    title: '',
    message: '',
    scheduledFor: '',
    repeatType: 'once',
    notificationType: 'general',
    icon: '🔔'
  });

  const NOTIFICATION_TYPES = [
    { id: 'general', name: 'Général', icon: '🔔' },
    { id: 'health', name: 'Santé', icon: '💊' },
    { id: 'cycle', name: 'Cycle menstruel', icon: '🩸' },
    { id: 'contraception', name: 'Contraception', icon: '💜' },
    { id: 'appointment', name: 'Rendez-vous médecin', icon: '🏥' },
    { id: 'medication', name: 'Médicament', icon: '💉' },
    { id: 'education', name: 'Éducation', icon: '📚' },
    { id: 'girls_room', name: 'Girls Room', icon: '🌸' },
  ];

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, authLoading]);

  const fetchData = async () => {
    try {
      const res = await api.get('/notifications/all');
      setNotifications(res.data.notifications || []);
      setUpcomingReminders(res.data.upcomingReminders || []);
      setPastReminders(res.data.pastReminders || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les notifications',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notifId) => {
    try {
      await notificationsAPI.markAsRead(notifId);
      setNotifications(prev => 
        prev.map(n => n.id === notifId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const deleteNotification = async (notifId) => {
    try {
      await notificationsAPI.delete(notifId);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      toast({ title: 'Notification supprimée' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer',
        variant: 'destructive'
      });
    }
  };

  const clearAllRead = async () => {
    try {
      await api.delete('/notifications/clear-all');
      setNotifications(prev => prev.filter(n => !n.read));
      toast({ title: 'Notifications lues supprimées' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer',
        variant: 'destructive'
      });
    }
  };

  const createReminder = async () => {
    if (!reminderForm.title || !reminderForm.message || !reminderForm.scheduledFor) {
      toast({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs',
        variant: 'destructive'
      });
      return;
    }

    try {
      await api.post('/notifications/schedule', reminderForm);
      setShowNewReminderDialog(false);
      setReminderForm({
        title: '',
        message: '',
        scheduledFor: '',
        repeatType: 'once',
        notificationType: 'general',
        icon: '🔔'
      });
      fetchData();
      toast({ title: 'Rappel programmé!' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de créer le rappel',
        variant: 'destructive'
      });
    }
  };

  const updateReminder = async () => {
    if (!editingReminder) return;

    try {
      await api.put(`/reminders/${editingReminder.id}`, reminderForm);
      setEditingReminder(null);
      fetchData();
      toast({ title: 'Rappel mis à jour' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier',
        variant: 'destructive'
      });
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      await api.delete(`/reminders/${reminderId}`);
      setUpcomingReminders(prev => prev.filter(r => r.id !== reminderId));
      setPastReminders(prev => prev.filter(r => r.id !== reminderId));
      toast({ title: 'Rappel supprimé' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer',
        variant: 'destructive'
      });
    }
  };

  const openEditDialog = (reminder) => {
    setEditingReminder(reminder);
    setReminderForm({
      title: reminder.title,
      message: reminder.message,
      scheduledFor: reminder.scheduledFor ? new Date(reminder.scheduledFor).toISOString().slice(0, 16) : '',
      repeatType: reminder.repeatType || 'once',
      icon: reminder.icon || '🔔'
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'welcome': return '🌸';
      case 'checkin': return '🔥';
      case 'achievement': return '🏆';
      case 'badge': return '⭐';
      case 'period': return '🩸';
      case 'contraception': return '💊';
      case 'fertile_window': return '🌸';
      default: return '🔔';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-white p-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold text-white">Notifications</h1>
          <Dialog open={showNewReminderDialog} onOpenChange={setShowNewReminderDialog}>
            <DialogTrigger asChild>
              <button className="text-white p-2" data-testid="add-reminder-btn">
                <Plus className="w-6 h-6" />
              </button>
            </DialogTrigger>
            <DialogContent className="bg-purple-900 border-purple-700">
              <DialogHeader>
                <DialogTitle className="text-white">Nouveau rappel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-pink-200">Titre</Label>
                  <Input
                    value={reminderForm.title}
                    onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })}
                    placeholder="Ex: Rendez-vous médecin"
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-pink-200">Message</Label>
                  <Input
                    value={reminderForm.message}
                    onChange={(e) => setReminderForm({ ...reminderForm, message: e.target.value })}
                    placeholder="Détails du rappel..."
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-pink-200">Type de notification</Label>
                  <Select 
                    value={reminderForm.notificationType} 
                    onValueChange={(value) => {
                      const selectedType = NOTIFICATION_TYPES.find(t => t.id === value);
                      setReminderForm({ 
                        ...reminderForm, 
                        notificationType: value,
                        icon: selectedType?.icon || '🔔'
                      });
                    }}
                  >
                    <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-purple-800 border-purple-600">
                      {NOTIFICATION_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.icon} {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-pink-200">Date et heure</Label>
                  <Input
                    type="datetime-local"
                    value={reminderForm.scheduledFor}
                    onChange={(e) => setReminderForm({ ...reminderForm, scheduledFor: e.target.value })}
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-pink-200">Répétition</Label>
                  <Select 
                    value={reminderForm.repeatType} 
                    onValueChange={(value) => setReminderForm({ ...reminderForm, repeatType: value })}
                  >
                    <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-purple-800 border-purple-600">
                      <SelectItem value="once">Une seule fois</SelectItem>
                      <SelectItem value="daily">Quotidien</SelectItem>
                      <SelectItem value="weekly">Hebdomadaire</SelectItem>
                      <SelectItem value="monthly">Mensuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={createReminder} className="w-full bg-pink-500 hover:bg-pink-600">
                  Créer le rappel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6">
        {/* Stats Bar */}
        <div className="flex items-center justify-between mb-6 px-2">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-pink-300" />
            <span className="text-white">{unreadCount} non lu(es)</span>
          </div>
          {notifications.some(n => n.read) && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearAllRead}
              className="text-pink-300 hover:text-white hover:bg-white/10"
            >
              Effacer les lues
            </Button>
          )}
        </div>

        <Tabs defaultValue="notifications" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-white/10 mb-4">
            <TabsTrigger value="notifications" className="data-[state=active]:bg-pink-500">
              Notifications
            </TabsTrigger>
            <TabsTrigger value="reminders" className="data-[state=active]:bg-pink-500">
              Rappels
            </TabsTrigger>
          </TabsList>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-3">
            {notifications.length === 0 ? (
              <Card className="p-8 bg-white/10 border-white/20 text-center">
                <BellOff className="w-12 h-12 text-pink-300 mx-auto mb-3" />
                <p className="text-white font-medium">Aucune notification</p>
                <p className="text-pink-200 text-sm">Vos notifications apparaîtront ici</p>
              </Card>
            ) : (
              notifications.map((notif) => (
                <Card 
                  key={notif.id}
                  className={`p-4 border-0 transition-all ${
                    notif.read 
                      ? 'bg-white/5' 
                      : 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-l-4 border-pink-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{notif.icon || getNotificationIcon(notif.type)}</span>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold">{notif.title}</h4>
                      <p className="text-pink-200 text-sm">{notif.message}</p>
                      <p className="text-pink-300 text-xs mt-1">{formatDate(notif.time)}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!notif.read && (
                        <button 
                          onClick={() => markAsRead(notif.id)}
                          className="p-2 text-green-400 hover:bg-white/10 rounded-lg"
                          title="Marquer comme lu"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button 
                        onClick={() => deleteNotification(notif.id)}
                        className="p-2 text-red-400 hover:bg-white/10 rounded-lg"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders" className="space-y-4">
            {/* Upcoming Reminders */}
            <div>
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <BellRing className="w-4 h-4 text-pink-300" />
                À venir ({upcomingReminders.length})
              </h3>
              {upcomingReminders.length === 0 ? (
                <Card className="p-6 bg-white/5 border-white/10 text-center">
                  <Clock className="w-8 h-8 text-pink-300 mx-auto mb-2" />
                  <p className="text-pink-200 text-sm">Aucun rappel programmé</p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {upcomingReminders.map((reminder) => (
                    <Card 
                      key={reminder.id}
                      className="p-4 bg-gradient-to-r from-purple-600/30 to-pink-600/30 border-0"
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{reminder.icon || '🔔'}</span>
                        <div className="flex-1">
                          <h4 className="text-white font-semibold">{reminder.title}</h4>
                          <p className="text-pink-200 text-sm">{reminder.message}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-pink-300">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(reminder.scheduledFor)}
                            </span>
                            {reminder.repeatType !== 'once' && (
                              <span className="px-2 py-0.5 bg-white/10 rounded">
                                {reminder.repeatType === 'daily' ? 'Quotidien' : 
                                 reminder.repeatType === 'weekly' ? 'Hebdo' : 'Mensuel'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button 
                            onClick={() => openEditDialog(reminder)}
                            className="p-2 text-blue-400 hover:bg-white/10 rounded-lg"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => deleteReminder(reminder.id)}
                            className="p-2 text-red-400 hover:bg-white/10 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Past Reminders */}
            {pastReminders.length > 0 && (
              <div className="mt-6">
                <h3 className="text-white/60 font-semibold mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Passés ({pastReminders.length})
                </h3>
                <div className="space-y-2 opacity-60">
                  {pastReminders.slice(0, 5).map((reminder) => (
                    <Card 
                      key={reminder.id}
                      className="p-3 bg-white/5 border-0"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{reminder.icon || '🔔'}</span>
                        <div className="flex-1">
                          <h4 className="text-white text-sm">{reminder.title}</h4>
                          <p className="text-pink-300 text-xs">{formatDate(reminder.scheduledFor)}</p>
                        </div>
                        <button 
                          onClick={() => deleteReminder(reminder.id)}
                          className="p-2 text-red-400/50 hover:bg-white/10 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit Reminder Dialog */}
        <Dialog open={!!editingReminder} onOpenChange={(open) => !open && setEditingReminder(null)}>
          <DialogContent className="bg-purple-900 border-purple-700">
            <DialogHeader>
              <DialogTitle className="text-white">Modifier le rappel</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-pink-200">Titre</Label>
                <Input
                  value={reminderForm.title}
                  onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })}
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-pink-200">Message</Label>
                <Input
                  value={reminderForm.message}
                  onChange={(e) => setReminderForm({ ...reminderForm, message: e.target.value })}
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-pink-200">Date et heure</Label>
                <Input
                  type="datetime-local"
                  value={reminderForm.scheduledFor}
                  onChange={(e) => setReminderForm({ ...reminderForm, scheduledFor: e.target.value })}
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-pink-200">Répétition</Label>
                <Select 
                  value={reminderForm.repeatType} 
                  onValueChange={(value) => setReminderForm({ ...reminderForm, repeatType: value })}
                >
                  <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-purple-800 border-purple-600">
                    <SelectItem value="once">Une seule fois</SelectItem>
                    <SelectItem value="daily">Quotidien</SelectItem>
                    <SelectItem value="weekly">Hebdomadaire</SelectItem>
                    <SelectItem value="monthly">Mensuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={updateReminder} className="flex-1 bg-pink-500 hover:bg-pink-600">
                  Enregistrer
                </Button>
                <Button 
                  onClick={() => setEditingReminder(null)} 
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Annuler
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <BottomNav active="notifications" />
    </div>
  );
};

export default NotificationsManager;
